import { useState } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Modal, ModalContent } from '@amzn/stencil-react-components/modal';
import { getMediaPreviewKind, MediaPreviewKind } from '../domain/mediaPreviewKind';

const PREVIEW_MAX_HEIGHT = '70vh';

interface MediaPreviewModalProps {
  isOpen: boolean;
  /** File name shown as the modal title and used to pick the preview kind. */
  fileName: string;
  /** Presigned URL of the media object. */
  url: string;
  /** Copy for the unsupported-type fallback body. */
  unsupportedText: string;
  /** Label for the download button (also the fallback primary action). */
  downloadLabel: string;
  onClose: () => void;
}

/**
 * In-browser previewer for uploaded media, shared across features (task
 * attachments, audit responses). Images render full-width; every other
 * file type shows explanation copy plus a download button — in-browser
 * playback of videos/documents is codec- and browser-dependent (e.g.
 * HEVC-encoded .mp4 plays on macOS but not in Chrome), so downloads are
 * the reliable path.
 *
 * A download link is always offered — the presigned URLs carry
 * `response-content-disposition=attachment`, so navigation saves the file.
 */
export function MediaPreviewModal({
  isOpen,
  fileName,
  url,
  unsupportedText,
  downloadLabel,
  onClose,
}: MediaPreviewModalProps): JSX.Element {
  // Playback can fail even for supported containers when the codec inside
  // is undecodable in the browser (e.g. HEVC-encoded .mp4 from iPhones —
  // plays natively on macOS but not in Chrome). Fall back to the
  // download view instead of a dead player.
  const [mediaFailed, setMediaFailed] = useState(false);

  // Reset the failure flag when a different file is previewed
  // (render-time state adjustment, per the hooks-extra lint rule).
  const [seededUrl, setSeededUrl] = useState(url);
  if (url !== seededUrl) {
    setSeededUrl(url);
    setMediaFailed(false);
  }

  const canPreview =
    !mediaFailed && getMediaPreviewKind(fileName) === MediaPreviewKind.IMAGE;

  return (
    <Modal isOpen={isOpen} close={onClose} aria-label={fileName}>
      <ModalContent titleText={fileName} onCloseButtonClick={onClose}>
        <Col gridGap="dimensions.spacing.300" padding="dimensions.spacing.200">
          {canPreview ? (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- onError is a resource-load event, not a user interaction
            <img
              src={url}
              alt={fileName}
              onError={() => setMediaFailed(true)}
              css={{
                maxWidth: '100%',
                maxHeight: PREVIEW_MAX_HEIGHT,
                objectFit: 'contain',
              }}
            />
          ) : (
            <Text>{unsupportedText}</Text>
          )}

          <Row justifyContent="flex-end">
            <Button
              variant={canPreview ? ButtonVariant.Secondary : ButtonVariant.Primary}
              onClick={() => window.open(url, '_blank', 'noopener')}
            >
              {downloadLabel}
            </Button>
          </Row>
        </Col>
      </ModalContent>
    </Modal>
  );
}
